import { useState } from 'react';
import { usePluginStore } from '@/stores/plugin-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { resolveIcon } from '@/constants/icons';
import { ChevronRight, Blocks } from 'lucide-react';
import type { PluginClientConfig, PanelDefinition } from '@vtak/shared';

/** Category display configuration */
const CATEGORY_CONFIG = new Map([
  ['space', { label: 'Space Domain', color: 'text-purple-400', bg: 'bg-purple-500/10' }],
  ['force-protect', { label: 'Force Protection', color: 'text-blue-400', bg: 'bg-blue-500/10' }],
  ['patrol', { label: 'Law Enforcement', color: 'text-cyan-400', bg: 'bg-cyan-500/10' }],
  ['incident', { label: 'Incident Management', color: 'text-yellow-400', bg: 'bg-yellow-500/10' }],
  ['maritime', { label: 'Maritime', color: 'text-teal-400', bg: 'bg-teal-500/10' }],
  ['ground', { label: 'Ground Operations', color: 'text-green-400', bg: 'bg-green-500/10' }],
  ['air-ops', { label: 'Air Operations', color: 'text-sky-400', bg: 'bg-sky-500/10' }],
]);

/** Default category config for unknown plugins */
const DEFAULT_CATEGORY = { label: 'Other', color: 'text-gray-400', bg: 'bg-gray-500/10' };

/** Plugins hub panel - categorized access to all plugin panels */
export function PluginsHubPanel(): React.JSX.Element {
  const activePlugins = usePluginStore((s) => s.activePlugins);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <HubHeader pluginCount={activePlugins.length} />
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {activePlugins.map((plugin) => (
          <PluginSection key={plugin.id} plugin={plugin} />
        ))}
      </div>
    </div>
  );
}

/** Hub header with plugin count */
function HubHeader({ pluginCount }: { pluginCount: number }): React.JSX.Element {
  return (
    <div className="flex items-center gap-3 border-b border-echo-border px-4 py-3">
      <Blocks className="h-4 w-4 text-ai" />
      <div className="flex-1">
        <span className="text-sm font-semibold text-foreground">Plugins</span>
      </div>
      <span className="rounded-full bg-ai/15 px-2 py-0.5 text-[10px] font-semibold text-ai">
        {pluginCount} active
      </span>
    </div>
  );
}

/** Single plugin section with its panels */
function PluginSection({ plugin }: { plugin: PluginClientConfig }): React.JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const category = CATEGORY_CONFIG.get(plugin.id) ?? DEFAULT_CATEGORY;

  return (
    <div className="rounded-lg border border-echo-border/50 overflow-hidden">
      <SectionHeader
        plugin={plugin}
        category={category}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />
      {expanded && (
        <div className="border-t border-echo-border/30 bg-white/[0.02]">
          {plugin.panels.map((panel) => (
            <PanelButton
              key={panel.id}
              pluginId={plugin.id}
              panel={panel}
            />
          ))}
          {plugin.mapLayers.length > 0 && (
            <LayerInfo layerCount={plugin.mapLayers.length} />
          )}
        </div>
      )}
    </div>
  );
}

/** Plugin section header */
function SectionHeader({
  plugin,
  category,
  expanded,
  onToggle,
}: {
  plugin: PluginClientConfig;
  category: { label: string; color: string; bg: string };
  expanded: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
    >
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${category.bg}`}>
        <Blocks className={`h-4 w-4 ${category.color}`} />
      </div>
      <div className="flex-1 text-left">
        <p className="text-[13px] font-semibold text-foreground">{plugin.name}</p>
        <p className="text-[10px] text-muted-foreground">{category.label}</p>
      </div>
      <ChevronRight
        className={`h-4 w-4 text-muted-foreground/40 transition-transform ${expanded ? 'rotate-90' : ''}`}
      />
    </button>
  );
}

/** Panel navigation button within a plugin section */
function PanelButton({
  pluginId,
  panel,
}: {
  pluginId: string;
  panel: PanelDefinition;
}): React.JSX.Element {
  const setActivePanel = useSidebarStore((s) => s.setActivePanel);
  const panelId = `${pluginId}:${panel.id}`;
  const Icon = resolveIcon(panel.icon);

  return (
    <button
      onClick={() => setActivePanel(panelId)}
      className="group flex w-full items-center gap-3 px-4 py-2 transition-colors hover:bg-white/[0.04] active:bg-white/[0.06]"
    >
      <Icon className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
      <span className="flex-1 text-left text-[13px] text-foreground/80 group-hover:text-foreground">
        {panel.label}
      </span>
      <ChevronRight className="h-3 w-3 text-muted-foreground/20 group-hover:text-muted-foreground/60" />
    </button>
  );
}

/** Map layer count indicator */
function LayerInfo({ layerCount }: { layerCount: number }): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 border-t border-echo-border/20">
      <span className="text-[9px] text-muted-foreground/50 uppercase tracking-wider">
        {layerCount} map {layerCount === 1 ? 'layer' : 'layers'}
      </span>
    </div>
  );
}
